import { WORKFLOW_CANVAS as C, type AddStepPresetId, type WorkflowStepType, type WorkflowTrigger } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, cn, cssVars, Icon, type CssVarMap } from "@sagot/ui";
import { useEffect, useEffectEvent, useId, useMemo, useState, type FocusEvent, type KeyboardEvent } from "react";
import { useProblems } from "../builder/problems-context";
import { usePathLimit } from "../builder/system/path-limit";
import type { Definition } from "../types";
import { canvasSize, edgesOf, tidyLayout, TRIGGER_TILE, withOffsets, type Point } from "./canvas-layout";
import { CanvasEdges } from "./canvas-edges";
import { PathTile, TriggerTile } from "./canvas-tile";
import { PathJumpBar } from "./path-jump-bar";
import { ConnectorContext } from "./connector-context";
import { tileRows } from "./connector-geometry";
import { useCanvasConnectors } from "./use-canvas-connectors";
import { usePanZoom } from "./use-pan-zoom";

const K = "engage.workflows.canvas";
const PERCENT = 100;
const px = (value: number): string => `${String(value)}px`;
/** Sizes of the parts of a tile, read by the tiles through `var(--geo-*)`. */
const ROW_VARS: CssVarMap = {
  "--geo-header": px(C.headerHeight),
  "--geo-step": px(C.stepHeight),
  "--geo-step-gap": px(C.stepGap),
  "--geo-pad": px(C.tilePadding),
  "--geo-footer": px(C.footerHeight),
  "--geo-exit": px(C.exitHeight),
  "--geo-indent": px(C.exitIndent),
  "--geo-handle": px(C.handleSize),
  // How far a handle sits from the right end of its row so that its centre is on the tile's border.
  "--geo-handle-x": px(-(C.tilePadding + C.handleSize / 2)),
};

interface CanvasViewProps {
  trigger: WorkflowTrigger;
  definition: Definition;
  disabled: boolean;
  /** A path to bring into view as soon as the canvas has drawn it (a copy the author just made); `onRevealed` says it was done. */
  reveal: string | null;
  onRevealed: () => void;
  onEdit: (path: string, index: number) => void;
  onAdd: (path: string, type: WorkflowStepType, preset?: AddStepPresetId) => void;
  onReplace: (path: string, type: WorkflowStepType, preset?: AddStepPresetId) => void;
  onAddPath: () => void;
  onOpenTrigger: () => void;
  /** Connections made on the canvas change the definition like any edit in the list view. */
  onChange: (definition: Definition) => void;
}

/**
 * The canvas: the same workflow as path tiles joined by connectors. Click a step to open the step sheet; drag the
 * background or a tile header, scroll, or use the arrow keys to move around; zoom with the buttons, `+` / `-`, or Ctrl + wheel.
 * The list view stays the accessible equivalent, and the position of a tile is never saved with the workflow.
 */
export function CanvasView({ trigger, definition, disabled, reveal, onRevealed, onEdit, onAdd, onReplace, onAddPath, onOpenTrigger, onChange }: CanvasViewProps) {
  const { t } = useTranslation();
  const hintId = useId();
  const { viewport, zoom, panning, handlers, zoomIn, zoomOut, resetZoom, scrollToStart, scrollToBox } = usePanZoom();
  const [offsets, setOffsets] = useState<Readonly<Record<string, Point>>>({});
  const boxes = useMemo(() => withOffsets(tidyLayout(definition, !disabled), offsets), [definition, disabled, offsets]);
  const edges = useMemo(() => edgesOf(definition), [definition]);
  const rows = useMemo(() => Object.fromEntries(Object.entries(definition.paths).map(([id, path]) => [id, tileRows(path, !disabled)])), [definition, disabled]);
  const size = canvasSize(boxes);
  const pathIds = Object.keys(definition.paths).sort();
  const pathLimit = usePathLimit();
  const place = (id: string, offset: Point) => { setOffsets((current) => ({ ...current, [id]: offset })); };
  const connectors = useCanvasConnectors({ definition, readOnly: disabled, boxes, rows, edges, zoom, viewport, scrollToBox, onChange, place });
  // Pan and zoom keys first; then the connector keys (Delete on a selected connector, Escape to deselect).
  const regionHandlers = { ...handlers, onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => { handlers.onKeyDown(event); connectors.onKeyDown(event); } };
  const problems = useProblems();
  const flaggedPaths = useMemo(() => new Set(Object.keys(definition.paths).filter((id) => (problems.byPath.get(id)?.length ?? 0) > 0)), [definition, problems]);
  // The path last worked in (a tile took focus, or the jump bar went there): the jump bar marks it.
  const [current, setCurrent] = useState<string | null>(null);
  const jump = (id: string) => {
    setCurrent(id);
    connectors.api.goTo(id);
  };
  const track = (event: FocusEvent<HTMLDivElement>) => {
    const tile = event.target.closest<HTMLElement>("[data-tile]")?.dataset.tile;
    if (tile && tile !== TRIGGER_TILE) setCurrent(tile);
  };
  // A copy the author just made is brought into view once the browser has drawn it.
  const revealCopy = useEffectEvent((id: string) => {
    jump(id);
    onRevealed();
  });
  useEffect(() => {
    if (reveal === null || !(reveal in boxes)) return undefined;
    const frame = window.requestAnimationFrame(() => { revealCopy(reveal); });
    return () => { window.cancelAnimationFrame(frame); };
  }, [reveal, boxes]);
  const tidy = () => {
    setOffsets({});
    scrollToStart();
  };
  const move = (id: string) => (offset: Point) => { setOffsets((current) => ({ ...current, [id]: offset })); };
  return (
    <section className="space-y-3" aria-label={t(`${K}.title`)} data-testid="workflow-canvas">
      <p id={hintId} className="text-xs text-fg-subtle">{t(`${K}.hint`)}</p>
      <div className="flex flex-wrap items-center gap-2" role="toolbar" aria-label={t(`${K}.toolbar`)}>
        <Button type="button" variant="secondary" size="sm" iconOnly aria-label={t(`${K}.zoomOut`)} disabled={zoom <= C.zoomMin} onClick={zoomOut}>
          <Icon name="minus" />
        </Button>
        <span className="min-w-12 text-center text-xs font-semibold tabular-nums" aria-live="polite" data-testid="canvas-zoom">{`${String(Math.round(zoom * PERCENT))}%`}</span>
        <Button type="button" variant="secondary" size="sm" iconOnly aria-label={t(`${K}.zoomIn`)} disabled={zoom >= C.zoomMax} onClick={zoomIn}>
          <Icon name="plus" />
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={resetZoom}>
          <Icon name="maximize" />
          {t(`${K}.resetZoom`)}
        </Button>
        <span className="mx-1 hidden h-5 w-px bg-line sm:block" aria-hidden="true" />
        <Button type="button" variant="secondary" size="sm" onClick={tidy}>
          <Icon name="layers" />
          {t(`${K}.tidyUp`)}
        </Button>
        <Button type="button" variant="secondary" size="sm" disabled={disabled || pathIds.length >= pathLimit} onClick={onAddPath}>
          <Icon name="plus" />
          {t("engage.workflows.builder.addPath")}
        </Button>
      </div>
      <p className="sr-only" role="status" aria-live="polite" data-testid="canvas-announcer">{connectors.announcement}</p>
      <ConnectorContext.Provider value={connectors.api}>
        <div
          ref={viewport}
          role="group"
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrollable region must be focusable so keyboard users can pan and zoom it (axe scrollable-region-focusable)
          tabIndex={0}
          aria-label={t(`${K}.region`)}
          aria-describedby={hintId}
          className={cn("relative h-[min(70vh,48rem)] touch-none overflow-auto rounded-lg border border-line bg-canvas", panning ? "cursor-grabbing" : "cursor-grab")}
          style={cssVars(ROW_VARS)}
          onFocusCapture={track}
          {...regionHandlers}
        >
          <div data-pan="" className="relative w-[var(--geo-sw)] h-[var(--geo-sh)]" style={cssVars({ "--geo-sw": px(size.width * zoom), "--geo-sh": px(size.height * zoom) })}>
            <div data-pan="" className="absolute left-0 top-0 h-[var(--geo-h)] w-[var(--geo-w)] origin-top-left scale-[var(--geo-zoom)]" style={cssVars({ "--geo-w": px(size.width), "--geo-h": px(size.height), "--geo-zoom": zoom })}>
              <CanvasEdges
                edges={edges}
                boxes={boxes}
                rows={rows}
                width={size.width}
                height={size.height}
                selected={connectors.selectedEdge}
              focused={connectors.focusedEdge}
                flaggedPaths={flaggedPaths}
                dimmed={connectors.dimmed}
                preview={connectors.preview}
                editable={!disabled}
                grip={connectors.grip}
                onSelect={connectors.onSelectEdge}
              />
              {boxes[TRIGGER_TILE] ? (
                <TriggerTile
                  trigger={trigger}
                  box={boxes[TRIGGER_TILE]}
                  channels={definition.settings.channels}
                  agentCount={definition.settings.agent_ids.length}
                  offset={offsets[TRIGGER_TILE] ?? { x: 0, y: 0 }}
                  zoom={zoom}
                  onMove={move(TRIGGER_TILE)}
                  onOpen={onOpenTrigger}
                />
              ) : null}
              {pathIds.map((id) => {
                const path = definition.paths[id];
                const box = boxes[id];
                if (!path || !box) return null;
                return (
                  <PathTile
                    key={id}
                    id={id}
                    path={path}
                    box={box}
                    trigger={trigger}
                    disabled={disabled}
                    offset={offsets[id] ?? { x: 0, y: 0 }}
                    zoom={zoom}
                    onMove={move(id)}
                    onEdit={(index) => { onEdit(id, index); }}
                    onAdd={(type, preset) => { onAdd(id, type, preset); }}
                    onReplace={(type, preset) => { onReplace(id, type, preset); }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </ConnectorContext.Provider>
      <PathJumpBar definition={definition} current={current} onJump={jump} className="sticky bottom-2 z-10 shadow-sm" />
    </section>
  );
}
